import * as i0 from '@angular/core';
import { Directive, Input, InjectionToken, PLATFORM_ID, Inject, NgModule } from '@angular/core';
import { ReplaySubject, Subject, merge, fromEvent } from 'rxjs';
import { switchMap, map, shareReplay, startWith } from 'rxjs/operators';
import { isPlatformBrowser } from '@angular/common';
let ButtonDirective = /*#__PURE__*/(() => {
  class ButtonDirective {
    set flowDirectoryOnly(directoriesOnly) {
      this._directoryOnly = directoriesOnly;
      this.setup();
    }
    set flowAttributes(attributes) {
      this._attributes = attributes;
      this.setup();
    }
    set flow(flow) {
      this._flow = flow;
      this.setup();
    }
    setup() {
      if (!this._flow) {
        return;
      }
      this._flow.assignBrowse(this.el.nativeElement, this._directoryOnly, this._flow.opts.singleFile, this._attributes);
    }
    constructor(el) {
      this.el = el;
      this._directoryOnly = false;
    }
    static {
      this.ɵfac = function ButtonDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || ButtonDirective)(i0.ɵɵdirectiveInject(i0.ElementRef));
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: ButtonDirective,
        selectors: [["", "flowButton", ""]],
        inputs: {
          flowDirectoryOnly: "flowDirectoryOnly",
          flowAttributes: "flowAttributes",
          flow: "flow"
        }
      });
    }
  }
  return ButtonDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let DropDirective = /*#__PURE__*/(() => {
  class DropDirective {
    set flow(flow) {
      this.flowJs = flow;
      if (!flow) {
        return;
      }
      this.enable();
    }
    enable() {
      this.flowJs?.assignDrop(this.el.nativeElement);
    }
    disable() {
      this.flowJs?.unAssignDrop(this.el.nativeElement);
    }
    constructor(el, renderer) {
      this.el = el;
      this.renderer = renderer;
    }
    ngOnInit() {
      this.renderer.listen('body', 'drop', event => event.preventDefault());
      this.renderer.listen('body', 'dragover', event => event.preventDefault());
    }
    static {
      this.ɵfac = function DropDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || DropDirective)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i0.Renderer2));
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: DropDirective,
        selectors: [["", "flowDrop", ""]],
        inputs: {
          flow: "flow"
        },
        exportAs: ["flowDrop"]
      });
    }
  }
  return DropDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let SrcDirective = /*#__PURE__*/(() => {
  class SrcDirective {
    set flowSrc(transfer) {
      this.fileReader = new FileReader();
      this.fileReader.readAsDataURL(transfer.flowFile.file);
      this.fileReader.onload = event => {
        const url = event.target.result;
        this.el.nativeElement.setAttribute('src', url);
      };
    }
    constructor(el) {
      this.el = el;
    }
    static {
      this.ɵfac = function SrcDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || SrcDirective)(i0.ɵɵdirectiveInject(i0.ElementRef));
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: SrcDirective,
        selectors: [["", "flowSrc", ""]],
        inputs: {
          flowSrc: "flowSrc"
        }
      });
    }
  }
  return SrcDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const FlowInjectionToken = new InjectionToken('Flow');
function flowFile2Transfer(flowFile) {
  return {
    id: flowFile.uniqueIdentifier,
    name: flowFile.name,
    progress: flowFile.progress(),
    averageSpeed: flowFile.averageSpeed,
    currentSpeed: flowFile.currentSpeed,
    size: flowFile.size,
    paused: flowFile.paused,
    error: flowFile.error,
    complete: flowFile.isComplete(),
    success: flowFile.isComplete() && !flowFile.error,
    timeRemaining: flowFile.timeRemaining(),
    flowFile
  };
}
let FlowDirective = /*#__PURE__*/(() => {
  class FlowDirective {
    set flowConfig(options) {
      if (isPlatformBrowser(this.platform)) {
        this.flowJs = new this.flowConstructor(options);
        this.flow$.next(this.flowJs);
      }
    }
    constructor(flowConstructor, platform) {
      this.flowConstructor = flowConstructor;
      this.platform = platform;
      this.flow$ = new ReplaySubject(1);
      this.pauseOrResumeEvent$ = new Subject();
      this.events$ = this.flow$.pipe(switchMap(flow => merge(this.flowEvents(flow), this.ngxFlowEvents())));
      this.transfers$ = this.events$.pipe(map(_ => this.flowJs.files), map((files = []) => ({
        transfers: files.map(flowFile => flowFile2Transfer(flowFile)),
        flow: this.flowJs,
        totalProgress: this.flowJs.progress()
      })), shareReplay(1));
      this.somethingToUpload$ = this.transfers$.pipe(map(state => state.transfers.some(file => !file.success), startWith(false)));
    }
    flowEvents(flow) {
      const events = [this.listenForEvent(flow, 'fileSuccess'), this.listenForEvent(flow, 'fileProgress'), this.listenForEvent(flow, 'fileAdded'), this.listenForEvent(flow, 'filesAdded'), this.listenForEvent(flow, 'filesSubmitted'), this.listenForEvent(flow, 'fileRemoved'), this.listenForEvent(flow, 'fileRetry'), this.listenForEvent(flow, 'fileError'), this.listenForEvent(flow, 'uploadStart'), this.listenForEvent(flow, 'complete'), this.listenForEvent(flow, 'progress')];
      return merge(...events);
    }
    ngxFlowEvents() {
      const pauseOrResumeEvent$ = this.pauseOrResumeEvent$.pipe(map(_ => ({
        type: 'pauseOrResume'
      })));
      const newFlowInstanceEvent$ = this.flow$.pipe(map(_ => ({
        type: 'newFlowJsInstance'
      })));
      const events = [pauseOrResumeEvent$, newFlowInstanceEvent$];
      return merge(...events);
    }
    upload() {
      this.flowJs.upload();
    }
    cancel() {
      this.flowJs.cancel();
    }
    cancelFile(file) {
      file.flowFile.cancel();
    }
    pauseFile(file) {
      file.flowFile.pause();
      this.pauseOrResumeEvent$.next();
    }
    resumeFile(file) {
      file.flowFile.resume();
      this.pauseOrResumeEvent$.next();
    }
    listenForEvent(flow, eventName) {
      return fromEvent(flow, eventName).pipe(map(args => ({
        type: eventName,
        event: args
      })));
    }
    static {
      this.ɵfac = function FlowDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || FlowDirective)(i0.ɵɵdirectiveInject(FlowInjectionToken), i0.ɵɵdirectiveInject(PLATFORM_ID));
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: FlowDirective,
        selectors: [["", "flowConfig", ""]],
        inputs: {
          flowConfig: "flowConfig"
        },
        exportAs: ["flow"]
      });
    }
  }
  return FlowDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const directives = [ButtonDirective, SrcDirective, DropDirective, FlowDirective];
// export function flowFactory() {
//   return Flow;
// }
let NgxFlowModule = /*#__PURE__*/(() => {
  class NgxFlowModule {
    static {
      this.ɵfac = function NgxFlowModule_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || NgxFlowModule)();
      };
    }
    static {
      this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
        type: NgxFlowModule
      });
    }
    static {
      this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({});
    }
  }
  return NgxFlowModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/*
 * Public API Surface of ngx-flow
 */

/**
 * Generated bundle index. Do not edit.
 */

export { ButtonDirective, DropDirective, FlowDirective, FlowInjectionToken, NgxFlowModule, SrcDirective };
